import Information16 from '@carbon/icons-react/es/information/16';
import { FormInstance, Space, Tooltip } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Role, RoleType, UserType } from 'api/models';
import { Checkbox as CheckboxAtom } from 'components/atoms/checkbox';
import { Form as FormAtom } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Radio } from 'components/atoms/radio';
import { RadioGroup } from 'components/atoms/radio-buttons';
import { useTranslate } from 'hooks/use-translation';
import { useUsersQuery } from 'queries/members';
import { useGetAllPartnerCompanies } from 'queries/partner';
import { RoleSettingsTab } from './types';

interface Props {
  form: FormInstance;
  roles: Role[];
  view: RoleSettingsTab;
  isEdit?: boolean;
  roleId?: number;
}
const FormItemWithZeroMarginAndPadding = styled(FormAtom.Item)``;
const Form = styled(FormAtom)`
  ${FormItemWithZeroMarginAndPadding} {
    padding-bottom: 0;
    margin-bottom: 0;
  }
`;
const Checkbox = styled(CheckboxAtom)`
  display: flex;
  align-items: center;
`;
const CentreAlignWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

export interface RoleFormValues {
  roleName: string;
  use_as_placeholder: boolean;
  usedByPartners?: boolean;
  type: RoleType;
}

export const RolesForm: FC<Props> = ({ form, roles, view, isEdit, roleId }) => {
  const translate = useTranslate();
  const { data: users } = useUsersQuery();
  const disableAvailableToPartnerCheckbox = useMemo(() => {
    if (isEdit && roleId != null) {
      return users?.some(
        ({ userType, role }) =>
          userType === UserType.Partner && role?.roleId === roleId,
      );
    }

    return false;
  }, [isEdit, roleId, users]);
  const currentRoleName = (form.getFieldValue('roleName') || '').toLowerCase();
  const { data: partners } = useGetAllPartnerCompanies();
  const isUseByPartnerAvailable = useMemo(
    () =>
      (partners?.length ?? 0) > 0 ||
      !!roles.find((role) => role.usedByPartners === true),
    [partners?.length, roles],
  );
  const getRoleNames = useCallback(() => {
    return Object.values(roles).reduce<string[]>(
      (prev, current) =>
        currentRoleName === current.roleName?.toLowerCase()
          ? prev
          : [...prev, current.roleName?.toLowerCase()],
      [],
    );
  }, [currentRoleName, roles]);
  const [hideUseByPartnersCheckbox, setHideUseByPartnersCheckbox] = useState(
    !isUseByPartnerAvailable || view === RoleSettingsTab.Customer,
  );
  const onTypeChange = useCallback(
    (event) => {
      const type = event.target.value;

      form.setFieldsValue({
        type: event.target.value,
      });
      setHideUseByPartnersCheckbox(
        !isUseByPartnerAvailable || type === RoleType.Customer,
      );
    },
    [form, isUseByPartnerAvailable],
  );

  return (
    <Form layout="vertical" form={form} autoComplete="off">
      <FormAtom.Item
        name="type"
        required
        label={<FormattedMessage id="type" />}
      >
        <RadioGroup disabled={isEdit} onChange={onTypeChange}>
          <Space direction="vertical">
            <Radio value={RoleType.Vendor}>
              <FormattedMessage id="settings.roles.tabs.vendor" />
            </Radio>
            <Radio value={RoleType.Customer}>
              <FormattedMessage id="settings.roles.tabs.customer" />
            </Radio>
          </Space>
        </RadioGroup>
      </FormAtom.Item>
      <FormAtom.Item
        required
        rules={[
          { required: true, message: translate('input.required') },
          ({ isFieldTouched }) => ({
            validator: (_, value) => {
              if (!isFieldTouched('roleName')) {
                return Promise.resolve();
              }
              if (getRoleNames().includes(value?.toLowerCase())) {
                return Promise.reject(new Error(`${value} already exists`));
              }
              if (
                value.length !== 0 &&
                value?.replaceAll(' ', '').length === 0
              ) {
                return Promise.reject(
                  new Error(translate('settings.role.name.cannot.be.empty')),
                );
              }

              return Promise.resolve();
            },
          }),
        ]}
        name="roleName"
        label={<FormattedMessage id="role_name" />}
      >
        <Input
          placeholder={translate(
            'settings.placeholder.form.fields.name.placeholder',
          )}
        />
      </FormAtom.Item>
      <FormItemWithZeroMarginAndPadding
        name="use_as_placeholder"
        valuePropName="checked"
        initialValue
      >
        <Checkbox>
          <CentreAlignWrapper>
            <span>
              <FormattedMessage id="settings.roles.list.use_as_placeholder" />
            </span>
            <Tooltip
              title={
                <FormattedMessage id="settings.roles.list.use_as_placeholder.hint" />
              }
            >
              <Information16 />
            </Tooltip>
          </CentreAlignWrapper>
        </Checkbox>
      </FormItemWithZeroMarginAndPadding>
      <Tooltip
        title={
          disableAvailableToPartnerCheckbox ? (
            <FormattedMessage id="settings.roles.use_by_partners.disabled" />
          ) : null
        }
      >
        <FormItemWithZeroMarginAndPadding
          name="usedByPartners"
          valuePropName="checked"
          initialValue={false}
          hidden={hideUseByPartnersCheckbox}
        >
          <Checkbox disabled={disableAvailableToPartnerCheckbox}>
            <FormattedMessage id="settings.roles.use_by_partners.label" />
          </Checkbox>
        </FormItemWithZeroMarginAndPadding>
      </Tooltip>
    </Form>
  );
};
